import { ReactNode, useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const BRAND_COLORS = ["#1f4d3a", "#2f6b4f", "#d4af37", "#e8c766", "#f3ead9", "#c9bda6"];

type Props = {
  firstName?: string | null;
  unaName?: string | null;
  paymentComplete: boolean;
  associationCard?: ReactNode;
  addressCard?: ReactNode;
  paymentCard?: ReactNode;
  onRefresh: () => void;
};

export function CompletionCelebration({
  firstName,
  unaName,
  paymentComplete,
  associationCard,
  addressCard,
  paymentCard,
  onRefresh,
}: Props) {
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current || !paymentComplete) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    fired.current = true;
    confetti({
      particleCount: 140,
      spread: 90,
      startVelocity: 45,
      gravity: 0.9,
      ticks: 220,
      origin: { x: 0.5, y: 0 },
      colors: BRAND_COLORS,
      disableForReducedMotion: true,
    });
  }, [paymentComplete]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-2xl">
          <CheckCircle2 className="h-6 w-6 text-green-600" />
          Congratulations{firstName ? `, ${firstName}` : ""}!
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3 text-sm leading-relaxed">
          <p>Your onboarding is done. We have everything we need to take it from here.</p>
          <p>
            We will update you via email at each milestone from acquiring the EIN, to the
            registration submission, receiving the approval back from California, executing the
            documents in one glorious sitting and finally sending your documents via FedEx.
          </p>
          <p>You can login right back in to the portal at any time to see the progress.</p>
          <p>
            The whole process from here generally takes less than four weeks. Most of that is
            because the registration process in California is manual. We are subject to their
            timeframes.
          </p>
        </div>

        <p className="text-lg leading-relaxed">
          Now for the fun part, let's form <span className="font-semibold">{unaName}</span>
        </p>

        {associationCard}
        {addressCard}
        {paymentCard}

        {!paymentComplete && paymentCard ? (
          <Button variant="ghost" onClick={onRefresh} className="min-h-11">
            Just completed your payment? Refresh
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
